import { fixtureInks } from '../clubInks'
import { teamDisplayName, type TeamId } from '../teams'
import type { MatchForecast } from '../api/football'
import './WinProbabilityBar.css'

interface WinProbabilityBarProps {
  homeTeam: TeamId
  awayTeam: TeamId
  forecast: MatchForecast
}

/**
 * The provider's pre-match win/draw/away split, as one slim bar.
 *
 * It leads the picker, above the club names, and it is **static**: no entrance animation and no
 * re-render while the player steps the score. The moment a player is deciding is the moment they
 * want a sense of how hard the call is, and a bar that moves while they think is noise.
 *
 * The three percentages always total exactly 100 — normalised once, server-side, on write — so
 * this renders them verbatim rather than rounding again.
 *
 * Home and away take their club inks; the draw takes `--color-neutral-badge`, the same tone as the
 * `D` form badge below, so it never reads as a third team.
 */
function WinProbabilityBar({ homeTeam, awayTeam, forecast }: WinProbabilityBarProps) {
  const inks = fixtureInks(homeTeam, awayTeam)

  return (
    <div className="win-probability">
      <p className="win-probability__label label-section">Win probability</p>

      <div className="win-probability__figures" aria-hidden="true">
        <span className="win-probability__figure">{forecast.home}%</span>
        <span className="win-probability__figure win-probability__figure--draw">
          {forecast.draw}%
        </span>
        <span className="win-probability__figure">{forecast.away}%</span>
      </div>

      {/* One label for the whole bar: three separately-labelled segments read out as three
          disconnected numbers, and it is the comparison that carries the meaning. */}
      <div
        className="win-probability__bar"
        role="img"
        aria-label={
          `${teamDisplayName(homeTeam)} ${forecast.home}%, ` +
          `draw ${forecast.draw}%, ` +
          `${teamDisplayName(awayTeam)} ${forecast.away}%`
        }
      >
        <span
          className="win-probability__segment"
          style={{ width: `${forecast.home}%`, background: inks.home }}
        />
        <span
          className="win-probability__segment win-probability__segment--draw"
          style={{ width: `${forecast.draw}%` }}
        />
        <span
          className="win-probability__segment"
          style={{ width: `${forecast.away}%`, background: inks.away }}
        />
      </div>
    </div>
  )
}

export default WinProbabilityBar
